import { Component, Compiler, ViewChild, SystemJsNgModuleLoader, OnDestroy } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import {
    USER_ROLES,
    QUERY_PARAMS,
    TCI_COMPONENT_MODE,
    TCI_CONNECTION_ACTION_TYPE,
    TCI_APP_CUSTOMIZATION_TYPE,
    ITCIConnection,
    ITCITenantAppCategory,
    IConnVisibilityCategory,
    ITCIConnectionResponse,
    ITCIAppCustomizationParam,
    TCIMainMenu,
    TCITenantAppCategory,
    TCIConnectionVisibility,
    TCITenantSelectorType,
    TCITenantAppType,
    TCITenantConnectionsMenu,
    TCIConnectionServiceNotificationType
} from "@tci/common";

import { BaseConnectionDeleteComponentInput, BaseConnectionDeleteComponentOutput } from "./ng-connection-delete/base-connection-delete.component";
import { AbstractTciTenantComponentLoader } from "../ng-tenant/tci-tenant-component-loader.component";
import { TCITenantLoaderComponent } from "../ng-tenant/tci-tenant-component";

import { NgWaitService } from "../ng-shared/ng-components/ng-wait/ng-wait.service";
import { NgToastrService } from "../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { NgAppFeatureToggleService } from "../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgConnectionsService } from "./ng-connections.service";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { TciTenantModelService } from "../ng-tenant/tci-tenant.model";
import { TciTenantComponentCacheService, ITCITenantCacheOptions } from "../ng-tenant/tci-tenant-component-cache.service";
import { NgCustomizationProviderService } from "../ng-shared/ng-services/ng-customization-provider.service";

import { NgPaginatorComponent } from "../ng-shared/ng-components/ng-paginator/ng-paginator.component";
import {
    ICategoryFilterOptions,
    IConnVisibilityFilterOptions,
    getVisibilityFilterOptions,
    getConnectionFilterOptions,
    routeChildReduce
} from "../ng-shared/ng-util/tools";
import { BaseConnectionMenuComponentInput } from "./ng-connections-menu";
import { ITCIConnectionsMenuOutput } from "../ng-tenant/tci-tenant-components";

import { Observable, BehaviorSubject, Subscription, Subject } from "rxjs";
import { Dialog } from "primeng/primeng";
import * as lodash from "lodash";

const FILTER_LABEL_ALL = "All";
const CONN_SESSION_ITEM = "TCI-CONN-SESSION-DATA";

const CONN_SORTABLE_ROWS = {
    NAME: "name",
    CATEGORY: "category",
    CONNECTOR: "connector.name",
    CREATED: "created_dt",
    LAST_MODIFIED: "lastModified_dt",
    VISIBILITY: "visibility"
};

interface IStoredFilter {
    filter: { category: string, visibility: string, name: string };
    sort: { column: string, asc: boolean };
    page: number;
}

interface IRouterConnectorData {
    params: {
        inputid: string;
        routeid: string;
    };
    queryParams: {
        [key: string]: string;
    };
}

// #region
@TCITenantLoaderComponent({
    isdynamic: true
})
@Component({
    selector: "ng-tenant-dyn-cmp",
    template: "<template #dynamic></template>"
})
export class TCIConnectorDeleteDynLoaderComponent
    extends AbstractTciTenantComponentLoader<any, BaseConnectionDeleteComponentInput, BaseConnectionDeleteComponentOutput<any>> {
    constructor(
        loader: SystemJsNgModuleLoader,
        compiler: Compiler,
        tenantRemoteServices: TCITenantRemoteServices,
        tenantSvc: TciTenantService,
        tenantModelSvc: TciTenantModelService
    ) {
        super(loader, compiler, tenantRemoteServices, tenantSvc, tenantModelSvc);
    }
}
// #endregion

@Component({
    selector: "ng-connections",
    templateUrl: "./ng-connections.component.html",
    styleUrls: ["./ng-connections.component.less"]
})
export class NgConnectionsComponent implements OnDestroy {
    @ViewChild("pager") pager: NgPaginatorComponent;
    @ViewChild("dialogCreateConn") dialogCreateConn: Dialog;
    @ViewChild("dialogDeleteConn") dialogDeleteConn: Dialog;

    private filterInitialized = false;
    private loading = true;

    private connections: ITCIConnection[] = [];
    private connectionServerErrors: string[] = [];

    private deleteConnModalShow = false;
    private deleteConnInfo: ITCIConnection;
    private deleteConnDynCompInput: BaseConnectionDeleteComponentInput;
    private isBeingDeleted: { [key: string]: boolean } = {};

    // enable create option for ROR users
    private canShowCreateButton = false;

    // // Filter handlers
    private FILTER_CATEGORIES: ICategoryFilterOptions[] = [];
    private FILTER_VISIBILITY: IConnVisibilityFilterOptions[] = [];
    private filteredConns: ITCIConnection[] = [];
    private categoryFilter: string = FILTER_LABEL_ALL;
    private visibilityFilter: string = FILTER_LABEL_ALL;
    private nameFilterCtrl = new FormControl();

    // Pagination
    private pageStart = 0;
    private pages = 10;
    private paginatedRows$: BehaviorSubject<ITCIConnection[]> = new BehaviorSubject([]);
    private pageRestoreValue: number;

    // Sorting
    private SORTABLE_ROWS = CONN_SORTABLE_ROWS;
    private sortedBy: string = CONN_SORTABLE_ROWS.NAME;
    private sortAsc = true;

    // Custom Configs
    private CUSTOM_TYPES = TCI_APP_CUSTOMIZATION_TYPE;
    private customConfig: { [key: string]: ITCIAppCustomizationParam } = {};

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    // HM modal
    statusMenuSelectedOption: TCITenantConnectionsMenu;
    statusMenuCmpInput: BaseConnectionMenuComponentInput;

    // Connection create modal
    connectionSelectModalShow = false;

    constructor(
        private blockUiSvc: NgWaitService,
        private toastrSvc: NgToastrService,
        private defCustomSvc: NgCustomizationProviderService,
        private connSvc: NgConnectionsService,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private cacheSvc: TciTenantComponentCacheService,
        private userService: NgUserService,
        private featureToggleService: NgAppFeatureToggleService,
        private router: Router,
        private route: ActivatedRoute,
        private location: Location
    ) {
        this.initializeConnectionsList();

        // Initialize connectors build
        connSvc.getConnectors().subscribe();

        // Initialize providers subscriptions
        this.initProviders();

        // FE Custom Config Setup
        this.getCustomConfig();

        // check if user is read-only
        this.isUserROR();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private initializeConnectionsList(isReload = false): void {
        this.blockUiSvc.block("Loading...");

        this.connSvc.getConnections(isReload)
            .subscribe(connections => {
                this.blockUiSvc.unblock();
                this.loading = false;
                const connectionServerErrors = connections
                    .filter((connection: ITCIConnection) => connection.error)
                    .map((connection: ITCIConnection) => connection.error);

                if (connectionServerErrors.length > 0) {
                    console.error("There are problems getting all connections", connectionServerErrors);
                    this.connectionServerErrors = connectionServerErrors.map(error => error.message);
                }

                this.connections = lodash.uniqBy(connections.filter(connection => !connection.error), "id");
                this.generateLeftSideFilters();

                if (!this.filterInitialized) {
                    // Initialize name filtering actions
                    this.nameFilterCtrl.valueChanges
                        .debounceTime(300)
                        .subscribe(() => this.filterConns());

                    this.filterInitialized = true;
                }

                const filterStageData = sessionStorage.getItem(CONN_SESSION_ITEM);

                // Flago to know if the stored filter is available
                let storeFiltered = false;

                if (!lodash.isEmpty(filterStageData)) {
                    try {
                        const filterStage: IStoredFilter = JSON.parse(filterStageData);

                        // Restore sort
                        this.sortedBy = filterStage.sort.column;
                        this.sortAsc = filterStage.sort.asc;

                        this.pageRestoreValue = filterStage.page;

                        if (!lodash.isEmpty(filterStage.filter.name)) { // Restore filter by name
                            this.nameFilterCtrl.setValue(filterStage.filter.name);
                        }

                        // Restore filter by category
                        const category = filterStage.filter.category;
                        const connCategory = TCITenantAppCategory.fromId(category);

                        if (category !== FILTER_LABEL_ALL && !lodash.isUndefined(connCategory) && !lodash.isEmpty(connCategory)) {
                            this.filterByCategory(connCategory);
                            storeFiltered = true;
                        }

                        // Restore filter by visibility
                        const visibility = filterStage.filter.visibility;
                        const connVisibility = TCIConnectionVisibility.fromId(visibility);
                        if (visibility !== FILTER_LABEL_ALL && !lodash.isUndefined(connVisibility) && !lodash.isEmpty(connVisibility)) {
                            this.filterByVisibility(connVisibility);
                            storeFiltered = true;
                        }
                    } catch (e) {
                        console.error("Connections stage restore incomplete", e);
                        this.filterConns();
                        storeFiltered = true;
                    }
                }

                if (isReload || !storeFiltered) { // IPAS-16263
                    this.filterConns();
                }

                this.tenantSvc.onInit().subscribe(() => this.routeEventsHandling());
            });
    }

    // Route handling
    private routeEventsHandling() {
        if (this.router.routerState.snapshot.root && this.router.routerState.snapshot.root.firstChild) {
            this.getConnFromRoute(this.router.routerState.snapshot.root.firstChild.firstChild as any);
        }

        this.subscriptions.push(this.router.events
            .filter(evnt => evnt.url.startsWith("/connections"))
            .subscribe((evnt: any) => {
                if (evnt.state && (evnt.state.root || evnt.state._root)) {
                    const root = evnt.state.root || evnt.state._root;
                    const params: any = lodash.assign({}, routeChildReduce(root));
                    this.getConnFromRoute({ params, queryParams: root.queryParams });
                }
            }));
    }

    private getConnFromRoute(routeData: IRouterConnectorData) {
        const inputIdErr = lodash.isEmpty(lodash.get(routeData, "params.inputid", ""));
        const routeidErr = lodash.isEmpty(lodash.get(routeData, "params.routeid", ""));
        const queryParamsErr = lodash.isEmpty(lodash.get(routeData, "queryParams", ""));
        const warn = () => {
            console.error("Connection error > Not available");
            this.router.navigate(["../connections"]);
        };
        if (inputIdErr && routeidErr && queryParamsErr) {
            delete this.statusMenuSelectedOption;
            delete this.statusMenuCmpInput;
        } else if (inputIdErr || routeidErr || queryParamsErr) {
            warn();
        } else {
            const connection = this.connections
                .find(conn =>
                    conn.tenantType.equals(TCITenantAppType.fromString(routeData.queryParams[QUERY_PARAMS.APP_TYPE])) &&
                    conn.id === routeData.params.inputid
                );
            if (connection) {
                this.mountStatusMenuComponent(connection, routeData.params.routeid);
            } else { // The connector does not exist
                warn();
            }
        }
    }

    private mountStatusMenuComponent(connection: ITCIConnection, routeId: string) {
        this.connSvc.statusMenuOptions$
            .filter(opts => opts && opts.length > 0)
            .map(opts => opts.find(opt => opt.connection.id === connection.id) || { comps: [] as TCITenantConnectionsMenu[] })
            .map(opts => opts.comps.find(opt => opt.getRouteId() === routeId))
            .filter(statusMenuOption => !!statusMenuOption)

            .flatMap(statusMenuOption => {
                const tenantType = connection.tenantType;
                const opts: ITCITenantCacheOptions = {
                    routeId: TCIMainMenu.MAIN_MENU_CONNECTIONS.getRouteId(),
                    tenantType
                };
                return this.cacheSvc.getCache(false, opts)
                    .map(() => ({
                        component: this.cacheSvc.getComponentFromCache(tenantType, statusMenuOption.getComponentSelectorType()),
                        statusMenuOption
                    }));
            })
            .subscribe(res => {
                if (res.component) {
                    this.statusMenuSelectedOption = res.statusMenuOption;
                    this.statusMenuCmpInput = new BaseConnectionMenuComponentInput(
                        connection.tenantType,
                        res.statusMenuOption.getComponentSelectorType(),
                        connection,
                        this.userService,
                        this.featureToggleService,
                        TCI_COMPONENT_MODE.COMP_MODE_CONN_HAMBURGER,
                        undefined,
                        null
                    );
                } else {
                    console.error("Menu component not available", res.statusMenuOption);
                }
            });
    }

    statusMenuCmpClose() {
        this.router.navigate(["../connections"], { queryParams: {} });
    }

    evaluateStatusMenuOutput($event: ITCIConnectionsMenuOutput) {
        console.log("Evaluating HMOutput", $event);
        if ($event.closeModal === true) {
            this.statusMenuCmpClose();
        }

        if ($event.refresh === true) {
            this.initializeConnectionsList(true);
        }
    }

    // To refresh the list when a provider notification request it
    private initProviders() {
        this.tenantSvc.onInit().subscribe(() => {
            const providersSubjs: Subject<ITCIConnectionResponse>[] = [];
            lodash.forEach(this.tenantModelSvc.getTenants(), tenant => {
                const provider = tenant.getConnectionServiceProvider();
                if (provider) {
                    const connSubj = provider.getConnectionsSubject();
                    providersSubjs.push(connSubj);
                }
            });

            this.subscriptions.push(Observable.merge(...providersSubjs)
                .filter(res =>
                    [
                        TCIConnectionServiceNotificationType.NOTIFICATION_CONNECTION_ADDED,
                        TCIConnectionServiceNotificationType.NOTIFICATION_CONNECTOR_REMOVED
                    ].includes(res.notificationType)
                )
                .subscribe(() => this.initializeConnectionsList(true))
            );
        });
    }

    /**
     * <List filtering instructions>
     */
    private generateLeftSideFilters() {
        if (this.connections.length > 0) {
            this.FILTER_CATEGORIES = getConnectionFilterOptions(FILTER_LABEL_ALL)
                .map(filterOpt => {
                    if (filterOpt.category.id === FILTER_LABEL_ALL) {
                        filterOpt.counter = this.connections.length;
                    } else {
                        filterOpt.counter = this.connections
                            .filter(connection => TCITenantAppCategory.fromConnection(connection).id === filterOpt.category.id)
                            .length;
                    }
                    return filterOpt;
                })
                .filter(opt => opt.counter > 0);

            this.FILTER_VISIBILITY = getVisibilityFilterOptions(FILTER_LABEL_ALL)
                .map(filterOpt => {
                    if (filterOpt.category.label === FILTER_LABEL_ALL) {
                        filterOpt.counter = this.connections.length;
                    } else {
                        filterOpt.counter = this.connections
                            .filter(connection => connection.visibility.id === filterOpt.category.id)
                            .length;
                    }
                    return filterOpt;
                })
                .filter(opt => opt.counter > 0);
        }
    }

    private filterConns() {
        const filterMakeup = (f: string) => f.toString().toLowerCase().trim();
        const nameFilter = this.nameFilterCtrl.value || "";
        this.filteredConns = this.connections
            .filter(conn =>
                this.categoryFilter === FILTER_LABEL_ALL ||
                TCITenantAppCategory.fromConnection(conn).id === this.categoryFilter
            )
            .filter(conn =>
                this.visibilityFilter === FILTER_LABEL_ALL ||
                conn.visibility.id === this.visibilityFilter
            )
            .filter(conn => nameFilter === "" || filterMakeup(conn.name).indexOf(filterMakeup(nameFilter)) >= 0);

        if (this.pager) { // Reset the paginator current page
            this.pager.changePage(this.pageRestoreValue || 1);
            delete this.pageRestoreValue;
        }

        this.sort(this.sortedBy || CONN_SORTABLE_ROWS.NAME);
        this.storeFilterStage();
    }

    filterByCategory(opt: ITCITenantAppCategory) {
        const filteredCategory = opt.id === FILTER_LABEL_ALL ? FILTER_LABEL_ALL : opt.id;
        if (this.categoryFilter !== filteredCategory) {
            this.categoryFilter = filteredCategory;
            this.filterConns();
        }
    }

    filterByVisibility(opt: IConnVisibilityCategory) {
        const filteredVisibility = opt.id === FILTER_LABEL_ALL ? FILTER_LABEL_ALL : opt.id;
        if (this.visibilityFilter !== filteredVisibility) {
            this.visibilityFilter = filteredVisibility;
            this.filterConns();
        }
    }

    // <Paginator instructions>
    private setPaginatedRows() {
        const part = this.filteredConns.slice(this.pageStart, this.pageStart + this.pages);
        if (part) {
            if (part.length === 0 && this.pager) { // Deleted app from last page, "pager" could be gone if no app is present after filtering
                const prevPage = this.pager.getPage() - 1;
                if (prevPage > 0) {
                    this.pager.changePage(prevPage);
                }
            } else {
                this.paginatedRows$.next(part);
            }
        }
        this.storeFilterStage();
    }

    private onPageChange($event: number) {
        this.pageStart = $event;
        this.setPaginatedRows();
    }

    // <Sorting instructions>
    private sort(sortBy: string) {
        if (sortBy === CONN_SORTABLE_ROWS.CATEGORY) {
            this.sortByCatUtil(true);
        }

        // Sort straight first
        // IPAS-13258: Sorts in case sensitive order (ASCII sort) instead of using default sort.
        if (sortBy === CONN_SORTABLE_ROWS.NAME) {
            this.filteredConns = this.filteredConns.sort((a, b) => this.alphaNumSort(a.name.toString(), b.name.toString()));
        } else {
            this.filteredConns = lodash.sortBy(this.filteredConns, sortBy, "tenantType.val", CONN_SORTABLE_ROWS.NAME);
        }
        this.sortByCatUtil(false);

        if (!this.sortAsc) { // If sorting is DESC then reverse
            this.filteredConns.reverse();
        }

        this.setPaginatedRows();
    }

    toggleSort(sortBy: string) {
        this.sortAsc = !this.sortAsc;

        if (sortBy === CONN_SORTABLE_ROWS.CATEGORY) {
            this.sortByCatUtil(true);
        }
        this.sortedBy = sortBy;
        if (sortBy === CONN_SORTABLE_ROWS.NAME) {
            this.filteredConns = this.filteredConns.sort((a, b) => this.alphaNumSort(a.name.toString(), b.name.toString()));
        } else if (sortBy === CONN_SORTABLE_ROWS.VISIBILITY) {
            this.filteredConns = lodash.sortBy(this.filteredConns, sortBy, "visibility.label", CONN_SORTABLE_ROWS.NAME);
        } else {
            this.filteredConns = lodash.sortBy(this.filteredConns, sortBy, "tenantType.val", CONN_SORTABLE_ROWS.NAME);
        }
        this.sortByCatUtil(false);

        if (!this.sortAsc) {
            this.filteredConns.reverse();
        }

        this.setPaginatedRows();
        if (this.pager) {
            this.pager.changePage(1);
        }
    }

    /**
     * For "category" sorting the TCITenantAppCategory must be
     * part of the connections array, so here that data is bound
     * to each connection if "set" is TRUE and cleans it when FALSE
     */
    private sortByCatUtil(set: boolean) {
        this.filteredConns = this.filteredConns.map((conn: any) => {
            if (set) { // Temporary category attribute
                conn.category = TCITenantAppCategory.fromConnection(conn).label;
            } else { // Remove temporary category attribute
                delete conn.category;
            }
            return conn;
        });
    }

    private resetFilters() {
        this.categoryFilter = FILTER_LABEL_ALL;
        this.visibilityFilter = FILTER_LABEL_ALL;
        this.nameFilterCtrl.setValue("");
        this.filterConns();
    }

    private storeFilterStage() {
        const filters: IStoredFilter = {
            filter: {
                name: this.nameFilterCtrl.value,
                category: this.categoryFilter,
                visibility: this.visibilityFilter
            },
            sort: {
                column: this.sortedBy,
                asc: this.sortAsc
            },
            page: this.pager ? this.pager.getPage() : 1
        };
        sessionStorage.setItem(CONN_SESSION_ITEM, JSON.stringify(filters));
    }

    // <Delete connection>
    private deleteConnChoose($event: ITCIConnection) {
        this.deleteConnInfo = $event;
        const tenantType = $event.tenantType;
        const opts: ITCITenantCacheOptions = {
            routeId: TCIMainMenu.MAIN_MENU_CONNECTIONS.getRouteId(),
            tenantType
        };
        return this.cacheSvc.getCache(false, opts)
            .map(() => this.cacheSvc.getComponentFromCache(tenantType, TCITenantSelectorType.TCI_CONN_LIST_CONN_DELETE))
            .finally(() => this.deleteConnModalShow = true)
            .subscribe(comp => {
                if (!!comp) {
                    this.deleteConnDynCompInput = new BaseConnectionDeleteComponentInput(
                        $event.tenantType,
                        TCITenantSelectorType.TCI_CONN_LIST_CONN_DELETE,
                        $event
                    );
                } else {
                    delete this.deleteConnDynCompInput;
                }
            });
    }

    private deleteCancel() {
        this.deleteConnInfo = undefined;
        this.deleteConnModalShow = false;
    }

    private deleteConn() {
        this.deleteConnModalShow = false;
        this.tenantSvc.onInit()
            .map(() => this.tenantModelSvc.getTenant(this.deleteConnInfo.tenantType).getConnectionServiceProvider())
            .flatMap(provider => provider.deleteConnection({
                connection: this.deleteConnInfo,
                actionType: TCI_CONNECTION_ACTION_TYPE.CONN_DELETE
            }))
            .finally(() => this.blockUiSvc.unblock())
            .subscribe(res => {
                if (res.error) {
                    this.toastrSvc.error(`Connection "${this.deleteConnInfo.name}" was not deleted`, res.error);
                } else {
                    [this.connections, this.filteredConns].forEach(
                        collection => collection.splice(collection.findIndex(conn => conn.id === this.deleteConnInfo.id), 1)
                    );

                    this.setPaginatedRows();
                    this.generateLeftSideFilters();
                    this.toastrSvc.success(this.deleteConnInfo.name, "The connection has been deleted successfully");
                }
            });
    }

    /**
     * </List filtering instructions>
     */
    getIcon(conn: ITCIConnection) {
        if (conn.connector) {
            return conn.connector.smallIcon ? conn.connector.smallIcon : conn.connector.largeIcon;
        } else {
            return null;
        }
    }

    modalCenterCreateConn() {
        this.dialogCreateConn.center();
    }

    modalCenterDeleteConn() {
        this.dialogDeleteConn.center();
    }

    // Customizer
    private getCustomConfig() {
        const appListCustomTypes = [
            TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_FILTER,
            TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_COL_CATEGORY,
            TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_COL_VISIBILITY,
            TCI_APP_CUSTOMIZATION_TYPE.CONNECTIONS_LIST_ROW_HAMBURGER
        ];
        const customConfigObs: Observable<ITCIAppCustomizationParam>[] = appListCustomTypes
            .map(customType => {
                // Prepare observables to get the config of each type
                return this
                    .defCustomSvc
                    .getCustomizations({
                        type: customType
                    })
                    .map(custom => custom.success ? custom.result : null)
                    .map((customParam: ITCIAppCustomizationParam) => {
                        if (customParam) {
                            this.customConfig[customType] = customParam;
                        }
                        return customParam;
                    });
            });

        // Request the custom configuration
        Observable.merge(...customConfigObs)
            .take(appListCustomTypes.length)
            .toArray()
            .subscribe(() => { });
    }

    private getCustomVisible(type: TCI_APP_CUSTOMIZATION_TYPE): boolean {
        const custom = this.customConfig[type];
        if (!custom) {
            return false;
        }
        return this.customConfig[type].enabled && this.customConfig[type].visible;
    }

    private isUserROR() {
        this.subscriptions.push(
            this.userService.getUserInfo().subscribe(token => {
                if (token.rolX[0] === USER_ROLES.RO) {
                    this.canShowCreateButton = true;
                }
            })
        );
    }

    private alphaNumSort(comparison1: string, comparison2: string) {
        return comparison1.localeCompare(comparison2, "en", { numeric: true, caseFirst: "upper" });
    }
}
